import { useRouter } from 'next/router';
import { PropsWithChildren, createContext, useEffect, useState } from 'react';

export interface HistoryContextProps {
    history: string[];
    setHistory: (history: string[]) => void;
    back: (fallbackRoute?: string) => void;
}
export const HistoryContext = createContext({} as HistoryContextProps);

export const HistoryProvider = ({ children }: PropsWithChildren) => {
    const { asPath, push, isReady } = useRouter();
    const [history, setHistory] = useState<string[]>([]);

    useEffect(() => {
        if (isReady) {
            setHistory((prevHistory) => [...prevHistory, asPath]);
        }
    }, [asPath, isReady]);

    const back = (fallbackRoute?: string) => {
        const hasMultipleRoutes = history.length > 1;
        if (hasMultipleRoutes) {
            const prevRouteIndex = history.length - 2;
            // Last route is the current route, get penultimate route
            const prevRoute = history[prevRouteIndex];
            if (prevRoute !== asPath && !prevRoute.includes('#')) {
                push(prevRoute);
                const newHistory = history.slice(0, prevRouteIndex);
                setHistory(newHistory);
                return;
            }
        } else if (fallbackRoute) {
            push(fallbackRoute);
            return;
        } else if (history.length === 1 && history[0] === asPath) {
            // No history to go back to, fallback to home page
            push('/');
            return;
        }

        // Should be unreachable, but just in case
        push('/');
    };

    return (
        <HistoryContext.Provider
            value={{
                back,
                history,
                setHistory,
            }}
        >
            {children}
        </HistoryContext.Provider>
    );
};
